import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, Modal, ActivityIndicator, Image, Platform } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { Picker } from '@react-native-picker/picker';
import { DollarSign, Loader2, Calendar, Clock, CheckCircle2, X, Trash2, Edit3, Plus, Paperclip, Download, Eye, File, Image as ImageIcon, Video, FileText, Filter, Link2, AlertTriangle } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { downloadFile } from '../../lib/fileDownload';
import { ServiceProvider, ServiceRequestQuote, ServiceRequest } from './types';
import { QuoteFileUpload } from '../../components/QuoteFileUpload';
import { shadows } from '../../lib/styles';

const MarketResearchQuotes = () => {
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState<ServiceRequestQuote[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [quoteAttachments, setQuoteAttachments] = useState<Record<string, any[]>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  // Filter states
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [amountMinFilter, setAmountMinFilter] = useState<string>('');
  const [amountMaxFilter, setAmountMaxFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [associationFilter, setAssociationFilter] = useState<'all' | 'associated' | 'standalone'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal states
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showAssociateModal, setShowAssociateModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState<ServiceRequestQuote | null>(null);
  const [quoteToAssociate, setQuoteToAssociate] = useState<ServiceRequestQuote | null>(null);
  const [selectedServiceRequestId, setSelectedServiceRequestId] = useState<string>('');
  const [savingQuote, setSavingQuote] = useState(false);
  const [associatingQuote, setAssociatingQuote] = useState(false);
  const [deletingQuoteId, setDeletingQuoteId] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  
  const [quoteFormData, setQuoteFormData] = useState({
    service_provider_id: '',
    service_type: '',
    work_description: '',
    amount: '',
    quote_date: '',
    expires_at: '',
    notes: '',
    status: 'pending' as 'pending' | 'accepted' | 'rejected'
  });

  useEffect(() => {
    fetchQuotes();
    fetchServiceProviders();
    fetchServiceRequests();
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_request_quotes')
        .select(`
          *,
          service_provider:service_providers (
            id,
            company_name,
            email,
            phone,
            address
          ),
          service_request:service_requests (
            id,
            title
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data
      const attachmentsMap: Record<string, any[]> = {};
      const transformedQuotes = await Promise.all(
        (data || []).map(async (quote: any) => {
          // Fetch attachments
          const { data: attachmentsData } = await supabase
            .from('service_request_quote_attachments')
            .select('*')
            .eq('quote_id', quote.id)
            .order('created_at', { ascending: false });
          
          attachmentsMap[quote.id] = attachmentsData || [];

          return {
            ...quote,
            service_provider: Array.isArray(quote.service_provider) 
              ? quote.service_provider[0] 
              : quote.service_provider,
            service_request: Array.isArray(quote.service_request) 
              ? quote.service_request[0] 
              : quote.service_request
          };
        })
      );

      setQuotes(transformedQuotes);
      setQuoteAttachments(attachmentsMap);
    } catch (error: any) {
      console.error('Error fetching quotes:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      const { data, error } = await supabase
        .from('service_providers')
        .select('*')
        .order('company_name');

      if (error) throw error;
      setServiceProviders(data || []);
    } catch (error) {
      console.error('Error fetching service providers:', error);
    }
  };

  const fetchServiceRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select('id, title')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setServiceRequests(data || []);
    } catch (error) {
      console.error('Error fetching service requests:', error);
    }
  };

  const handleOpenQuoteModal = (quote?: ServiceRequestQuote) => {
    if (quote) {
      setEditingQuote(quote);
      setQuoteFormData({
        service_provider_id: quote.service_provider_id || '',
        service_type: quote.service_type || '',
        work_description: quote.work_description || '',
        amount: quote.amount?.toString() || '',
        quote_date: quote.quote_date || '',
        expires_at: quote.expires_at || '',
        notes: quote.notes || '',
        status: quote.status
      });
    } else {
      setEditingQuote(null);
      setQuoteFormData({
        service_provider_id: '',
        service_type: '',
        work_description: '',
        amount: '',
        quote_date: '',
        expires_at: '',
        notes: '',
        status: 'pending'
      });
    }
    setShowQuoteModal(true);
  };

  const handleSaveQuote = async () => {
    // Validate standalone quote requirements
    if (!editingQuote && !quoteFormData.service_type.trim()) {
      setError('Service type is required for standalone quotes');
      return;
    }
    if (!editingQuote && !quoteFormData.work_description.trim()) {
      setError('Work description is required for standalone quotes');
      return;
    }

    setSavingQuote(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const quoteData: any = {
        service_request_id: null, // Standalone quote
        service_provider_id: quoteFormData.service_provider_id || null,
        service_type: quoteFormData.service_type || null,
        work_description: quoteFormData.work_description || null,
        amount: quoteFormData.amount ? parseFloat(quoteFormData.amount) : null,
        quote_date: quoteFormData.quote_date || null,
        expires_at: quoteFormData.expires_at || null,
        notes: quoteFormData.notes || null,
        status: quoteFormData.status,
        created_by: user.id
      };

      // Can't accept standalone quotes
      if (!quoteData.service_request_id && quoteData.status === 'accepted') {
        quoteData.status = 'pending';
      }

      if (editingQuote) {
        const { error } = await supabase
          .from('service_request_quotes')
          .update(quoteData)
          .eq('id', editingQuote.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('service_request_quotes')
          .insert([quoteData]);

        if (error) throw error;
      }

      await fetchQuotes();
      
      setShowQuoteModal(false);
      setEditingQuote(null);
      setQuoteFormData({
        service_provider_id: '',
        service_type: '',
        work_description: '',
        amount: '',
        quote_date: '',
        expires_at: '',
        notes: '',
        status: 'pending'
      });
      setError(null);
    } catch (error: any) {
      console.error('Error saving quote:', error);
      setError(error.message);
    } finally {
      setSavingQuote(false);
    }
  };

  const handleDeleteQuote = async (quoteId: string) => {
    setDeletingQuoteId(quoteId);
    try {
      const { error } = await supabase
        .from('service_request_quotes')
        .delete()
        .eq('id', quoteId);

      if (error) throw error;

      await fetchQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
    } finally {
      setDeletingQuoteId(null);
    }
  };

  const handleAssociateQuote = async () => {
    if (!quoteToAssociate || !selectedServiceRequestId) return;

    setAssociatingQuote(true);
    try {
      const { error } = await supabase
        .from('service_request_quotes')
        .update({ service_request_id: selectedServiceRequestId })
        .eq('id', quoteToAssociate.id);

      if (error) throw error;

      await fetchQuotes();
      setShowAssociateModal(false);
      setQuoteToAssociate(null);
      setSelectedServiceRequestId('');
    } catch (error: any) {
      console.error('Error associating quote:', error);
      setError(error.message);
    } finally {
      setAssociatingQuote(false);
    }
  };

  const handleAcceptQuote = async (quoteId: string) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote || !quote.service_request_id) {
      setError('Quote must be associated with a service request to be accepted');
      return;
    }

    setUpdating(true);
    try {
      const { error: acceptError } = await supabase
        .from('service_request_quotes')
        .update({ status: 'accepted' })
        .eq('id', quoteId);

      if (acceptError) throw acceptError;

      // Reject all other pending quotes for the same service request
      const { error: rejectError } = await supabase
        .from('service_request_quotes')
        .update({ status: 'rejected' })
        .eq('service_request_id', quote.service_request_id)
        .eq('status', 'pending')
        .neq('id', quoteId);

      if (rejectError) {
        console.error('Error rejecting other quotes:', rejectError);
      }

      await fetchQuotes();
    } catch (error: any) {
      console.error('Error accepting quote:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleRejectQuote = async (quoteId: string) => {
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('service_request_quotes')
        .update({ status: 'rejected' })
        .eq('id', quoteId);

      if (error) throw error;

      await fetchQuotes();
    } catch (error: any) {
      console.error('Error rejecting quote:', error);
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const getQuoteStatusColor = (status: ServiceRequestQuote['status']) => {
    switch (status) {
      case 'accepted': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      case 'rejected': return { color: '#dc2626', backgroundColor: '#fee2e2' };
      default: return { color: '#ca8a04', backgroundColor: '#fef9c3' };
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const downloadAttachment = async (attachment: any) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-quote-attachments')
      .getPublicUrl(attachment.file_url);

    await downloadFile(publicUrl, attachment.file_name);
  };

  const viewImage = (attachment: any) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-quote-attachments')
      .getPublicUrl(attachment.file_url);
    setPreviewImage(publicUrl);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon size={16} color="#6b7280" />;
    if (fileType.startsWith('video/')) return <Video size={16} color="#6b7280" />;
    if (fileType === 'application/pdf') return <FileText size={16} color="#6b7280" />;
    return <File size={16} color="#6b7280" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImageFile = (fileType: string) => fileType.startsWith('image/');

  // Get unique service types from quotes
  const serviceTypes = Array.from(new Set(quotes.map(q => q.service_type).filter(Boolean))) as string[];

  // Filter quotes
  const filteredQuotes = quotes.filter(quote => {
    // Service type filter
    if (serviceTypeFilter !== 'all' && quote.service_type !== serviceTypeFilter) return false;
    
    // Provider filter
    if (providerFilter !== 'all' && quote.service_provider_id !== providerFilter) return false;
    
    // Amount filters
    if (amountMinFilter && quote.amount !== null && quote.amount < parseFloat(amountMinFilter)) return false;
    if (amountMaxFilter && quote.amount !== null && quote.amount > parseFloat(amountMaxFilter)) return false;
    
    // Date filters
    if (dateFromFilter && quote.quote_date && quote.quote_date < dateFromFilter) return false;
    if (dateToFilter && quote.quote_date && quote.quote_date > dateToFilter) return false;
    
    // Association filter
    if (associationFilter === 'associated' && !quote.service_request_id) return false;
    if (associationFilter === 'standalone' && quote.service_request_id) return false;
    
    // Status filter
    if (statusFilter !== 'all' && quote.status !== statusFilter) return false;
    
    return true;
  });

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <DollarSign size={24} color="#9ca3af" />
            <Text style={styles.title}>Market Research Quotes</Text>
          </View>
          <View style={styles.headerActions}>
            <Pressable
              style={styles.filterButton}
              onPress={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} color="#000000" />
              <Text style={styles.filterButtonText}>Filters</Text>
            </Pressable>
            <Pressable
              style={styles.addButton}
              onPress={() => handleOpenQuoteModal()}
            >
              <Plus size={16} color="#ffffff" />
              <Text style={styles.addButtonText}>Add Quote</Text>
            </Pressable>
            <Pressable
              style={styles.refreshButton}
              onPress={fetchQuotes}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#4b5563" />
              ) : (
                <Text style={styles.refreshButtonText}>Refresh</Text>
              )}
            </Pressable>
          </View>
        </View>

        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
            <Pressable onPress={() => setError(null)}>
              <Text style={styles.errorClose}>×</Text>
            </Pressable>
          </View>
        )}

        {/* Filters */}
        {showFilters && (
          <View style={styles.filtersContainer}>
            <WebScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.filtersGrid}>
                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Service Type</Text>
                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={serviceTypeFilter}
                      onValueChange={(value) => setServiceTypeFilter(value)}
                      style={styles.picker}
                    >
                      <Picker.Item label="All Types" value="all" />
                      {serviceTypes.map(type => (
                        <Picker.Item key={type} label={type} value={type} />
                      ))}
                    </Picker>
                  </View>
                </View>

                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Service Provider</Text>
                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={providerFilter}
                      onValueChange={(value) => setProviderFilter(value)}
                      style={styles.picker}
                    >
                      <Picker.Item label="All Providers" value="all" />
                      {serviceProviders.map(provider => (
                        <Picker.Item key={provider.id} label={provider.company_name} value={provider.id} />
                      ))}
                    </Picker>
                  </View>
                </View>

                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Min Amount</Text>
                  <TextInput
                    style={styles.filterInput}
                    value={amountMinFilter}
                    onChangeText={setAmountMinFilter}
                    placeholder="0.00"
                    keyboardType="numeric"
                  />
                </View>

                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Max Amount</Text>
                  <TextInput
                    style={styles.filterInput}
                    value={amountMaxFilter}
                    onChangeText={setAmountMaxFilter}
                    placeholder="0.00"
                    keyboardType="numeric"
                  />
                </View>

                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Date From</Text>
                  <TextInput
                    style={styles.filterInput}
                    value={dateFromFilter}
                    onChangeText={setDateFromFilter}
                    placeholder="YYYY-MM-DD"
                  />
                </View>

                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Date To</Text>
                  <TextInput
                    style={styles.filterInput}
                    value={dateToFilter}
                    onChangeText={setDateToFilter}
                    placeholder="YYYY-MM-DD"
                  />
                </View>

                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Association</Text>
                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={associationFilter}
                      onValueChange={(value) => setAssociationFilter(value as 'all' | 'associated' | 'standalone')}
                      style={styles.picker}
                    >
                      <Picker.Item label="All Quotes" value="all" />
                      <Picker.Item label="Associated Only" value="associated" />
                      <Picker.Item label="Standalone Only" value="standalone" />
                    </Picker>
                  </View>
                </View>

                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Status</Text>
                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={statusFilter}
                      onValueChange={(value) => setStatusFilter(value as 'all' | 'pending' | 'accepted' | 'rejected')}
                      style={styles.picker}
                    >
                      <Picker.Item label="All Statuses" value="all" />
                      <Picker.Item label="Pending" value="pending" />
                      <Picker.Item label="Accepted" value="accepted" />
                      <Picker.Item label="Rejected" value="rejected" />
                    </Picker>
                  </View>
                </View>
              </View>
            </WebScrollView>
          </View>
        )}

        {filteredQuotes.length === 0 ? (
          <View style={styles.emptyContainer}>
            <AlertTriangle size={48} color="#9ca3af" />
            <Text style={styles.emptyTitle}>No Quotes Found</Text>
            <Text style={styles.emptyText}>
              {quotes.length === 0
                ? 'There are no quotes in the system.'
                : 'No quotes match the selected filters.'}
            </Text>
          </View>
        ) : (
          <WebScrollView style={styles.quotesList}>
            {filteredQuotes.map((quote) => {
              const statusColors = getQuoteStatusColor(quote.status);
              return (
                <View key={quote.id} style={styles.quoteCard}>
                  <View style={styles.quoteHeader}>
                    <View style={styles.quoteContent}>
                      <View style={styles.quoteTitleRow}>
                        <Text style={styles.quoteProviderName}>
                          {quote.service_provider?.company_name || 'Unknown Provider'}
                        </Text>
                        <View style={[styles.statusBadge, { backgroundColor: statusColors.backgroundColor }]}>
                          <Text style={[styles.statusText, { color: statusColors.color }]}>
                            {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                          </Text>
                        </View>
                        {quote.service_request_id && (
                          <View style={styles.associatedBadge}>
                            <Text style={styles.associatedText}>Associated</Text>
                          </View>
                        )}
                        {!quote.service_request_id && (
                          <View style={styles.standaloneBadge}>
                            <Text style={styles.standaloneText}>Standalone</Text>
                          </View>
                        )}
                      </View>
                      {quote.service_type && (
                        <Text style={styles.quoteDetail}>
                          <Text style={styles.quoteDetailLabel}>Service Type:</Text> {quote.service_type}
                        </Text>
                      )}
                      {quote.work_description && (
                        <Text style={styles.quoteDescription}>{quote.work_description}</Text>
                      )}
                      {quote.service_request && (
                        <Text style={styles.quoteServiceRequest}>
                          <Text style={styles.quoteDetailLabel}>Service Request:</Text> {quote.service_request.title}
                        </Text>
                      )}
                      {quote.amount !== null && (
                        <Text style={styles.quoteAmount}>{formatCurrency(quote.amount)}</Text>
                      )}
                      {quote.notes && (
                        <Text style={styles.quoteNotes}>{quote.notes}</Text>
                      )}
                      <View style={styles.quoteMeta}>
                        {quote.quote_date && (
                          <View style={styles.metaItem}>
                            <Calendar size={12} color="#6b7280" />
                            <Text style={styles.metaText}>
                              Quote Date: {new Date(quote.quote_date).toLocaleDateString()}
                            </Text>
                          </View>
                        )}
                        {quote.expires_at && (
                          <View style={styles.metaItem}>
                            <Clock size={12} color="#6b7280" />
                            <Text style={styles.metaText}>
                              Expires: {new Date(quote.expires_at).toLocaleDateString()}
                            </Text>
                          </View>
                        )}
                        {quoteAttachments[quote.id] && quoteAttachments[quote.id].length > 0 && (
                          <View style={styles.metaItem}>
                            <Paperclip size={12} color="#6b7280" />
                            <Text style={styles.metaText}>
                              {quoteAttachments[quote.id].length} file{quoteAttachments[quote.id].length !== 1 ? 's' : ''}
                            </Text>
                          </View>
                        )}
                      </View>
                      {/* Attachments List */}
                      {quoteAttachments[quote.id] && quoteAttachments[quote.id].length > 0 && (
                        <View style={styles.attachmentsContainer}>
                          {quoteAttachments[quote.id].map((attachment: any) => (
                            <Pressable
                              key={attachment.id}
                              style={styles.attachmentItem}
                              onPress={() => downloadAttachment(attachment)}
                            >
                              <View style={styles.attachmentContent}>
                                {getFileIcon(attachment.file_type)}
                                <View style={styles.attachmentInfo}>
                                  <Text style={styles.attachmentName} numberOfLines={1}>
                                    {attachment.file_name}
                                  </Text>
                                  <Text style={styles.attachmentSize}>
                                    {formatFileSize(attachment.file_size)}
                                  </Text>
                                </View>
                              </View>
                              <View style={styles.attachmentActions}>
                                {isImageFile(attachment.file_type) && (
                                  <Pressable
                                    style={styles.attachmentAction}
                                    onPress={(e) => {
                                      e.stopPropagation();
                                      viewImage(attachment);
                                    }}
                                  >
                                    <Eye size={12} color="#6b7280" />
                                  </Pressable>
                                )}
                                <Pressable
                                  style={styles.attachmentAction}
                                  onPress={(e) => {
                                    e.stopPropagation();
                                    downloadAttachment(attachment);
                                  }}
                                >
                                  <Download size={12} color="#6b7280" />
                                </Pressable>
                              </View>
                            </Pressable>
                          ))}
                        </View>
                      )}
                    </View>
                    <View style={styles.quoteActions}>
                      {quote.service_request_id && quote.status !== 'accepted' && (
                        <Pressable
                          style={styles.quoteActionButton}
                          onPress={() => handleAcceptQuote(quote.id)}
                          disabled={updating}
                        >
                          <CheckCircle2 size={16} color="#16a34a" />
                        </Pressable>
                      )}
                      {!quote.service_request_id && (
                        <Pressable
                          style={styles.quoteActionButton}
                          onPress={() => {
                            setQuoteToAssociate(quote);
                            setShowAssociateModal(true);
                          }}
                        >
                          <Link2 size={16} color="#2563eb" />
                        </Pressable>
                      )}
                      {quote.status !== 'rejected' && (
                        <Pressable
                          style={styles.quoteActionButton}
                          onPress={() => handleRejectQuote(quote.id)}
                          disabled={updating}
                        >
                          <X size={16} color="#dc2626" />
                        </Pressable>
                      )}
                      <Pressable
                        style={styles.quoteActionButton}
                        onPress={() => handleOpenQuoteModal(quote)}
                      >
                        <Edit3 size={16} color="#6b7280" />
                      </Pressable>
                      <Pressable
                        style={styles.quoteActionButton}
                        onPress={() => handleDeleteQuote(quote.id)}
                        disabled={deletingQuoteId === quote.id}
                      >
                        {deletingQuoteId === quote.id ? (
                          <ActivityIndicator size="small" color="#6b7280" />
                        ) : (
                          <Trash2 size={16} color="#6b7280" />
                        )}
                      </Pressable>
                    </View>
                  </View>
                  <Text style={styles.quoteCreated}>
                    Created: {new Date(quote.created_at).toLocaleDateString()} at {new Date(quote.created_at).toLocaleTimeString()}
                  </Text>
                </View>
              );
            })}
          </WebScrollView>
        )}
      </View>

      {/* Add/Edit Quote Modal */}
      <Modal
        visible={showQuoteModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowQuoteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {editingQuote ? 'Edit Quote' : 'Add Market Research Quote'}
              </Text>
              <Pressable
                onPress={() => {
                  setShowQuoteModal(false);
                  setEditingQuote(null);
                  setQuoteFormData({
                    service_provider_id: '',
                    service_type: '',
                    work_description: '',
                    amount: '',
                    quote_date: '',
                    expires_at: '',
                    notes: '',
                    status: 'pending'
                  });
                  setError(null);
                }}
              >
                <X size={20} color="#9ca3af" />
              </Pressable>
            </View>

            <WebScrollView style={styles.modalBody} contentContainerStyle={styles.modalBodyContent}>
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Service Provider (Optional)</Text>
                <View style={styles.pickerContainer}>
                  <Picker
                    selectedValue={quoteFormData.service_provider_id}
                    onValueChange={(value) => setQuoteFormData({ ...quoteFormData, service_provider_id: value })}
                    style={styles.picker}
                  >
                    <Picker.Item label="None - Manual Entry" value="" />
                    {serviceProviders.map((provider) => (
                      <Picker.Item key={provider.id} label={provider.company_name} value={provider.id} />
                    ))}
                  </Picker>
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>
                  Service Type <Text style={styles.required}>*</Text>
                </Text>
                <TextInput
                  style={styles.formInput}
                  value={quoteFormData.service_type}
                  onChangeText={(value) => setQuoteFormData({ ...quoteFormData, service_type: value })}
                  placeholder="e.g., Plumbing, Electrical, HVAC"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>
                  Work Description <Text style={styles.required}>*</Text>
                </Text>
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  value={quoteFormData.work_description}
                  onChangeText={(value) => setQuoteFormData({ ...quoteFormData, work_description: value })}
                  placeholder="Describe the work being quoted..."
                  multiline
                  numberOfLines={3}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Amount (Optional)</Text>
                <TextInput
                  style={styles.formInput}
                  value={quoteFormData.amount}
                  onChangeText={(value) => setQuoteFormData({ ...quoteFormData, amount: value })}
                  placeholder="0.00"
                  keyboardType="numeric"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Quote Date (Optional)</Text>
                <TextInput
                  style={styles.formInput}
                  value={quoteFormData.quote_date}
                  onChangeText={(value) => setQuoteFormData({ ...quoteFormData, quote_date: value })}
                  placeholder="YYYY-MM-DD"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Expiration Date (Optional)</Text>
                <TextInput
                  style={styles.formInput}
                  value={quoteFormData.expires_at}
                  onChangeText={(value) => setQuoteFormData({ ...quoteFormData, expires_at: value })}
                  placeholder="YYYY-MM-DD"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Status</Text>
                <View style={styles.pickerContainer}>
                  <Picker
                    selectedValue={quoteFormData.status}
                    onValueChange={(value) => setQuoteFormData({ ...quoteFormData, status: value as 'pending' | 'accepted' | 'rejected' })}
                    style={styles.picker}
                  >
                    <Picker.Item label="Pending" value="pending" />
                    <Picker.Item label="Rejected" value="rejected" />
                  </Picker>
                </View>
                <Text style={styles.formHint}>Note: Quotes can only be accepted when associated with a service request</Text>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Notes (Optional)</Text>
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  value={quoteFormData.notes}
                  onChangeText={(value) => setQuoteFormData({ ...quoteFormData, notes: value })}
                  placeholder="Additional notes, terms, conditions..."
                  multiline
                  numberOfLines={4}
                />
              </View>

              {/* File Attachments - Only show when editing existing quote */}
              {editingQuote && editingQuote.id && (
                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Attachments</Text>
                  <QuoteFileUpload
                    quoteId={editingQuote.id}
                    disabled={savingQuote}
                  />
                </View>
              )}

              {error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              )}

              <View style={styles.modalActions}>
                <Pressable
                  style={styles.cancelButton}
                  onPress={() => {
                    setShowQuoteModal(false);
                    setEditingQuote(null);
                    setQuoteFormData({
                      service_provider_id: '',
                      service_type: '',
                      work_description: '',
                      amount: '',
                      quote_date: '',
                      expires_at: '',
                      notes: '',
                      status: 'pending'
                    });
                    setError(null);
                  }}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </Pressable>
                <Pressable
                  style={styles.saveButton}
                  onPress={handleSaveQuote}
                  disabled={savingQuote}
                >
                  {savingQuote ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.saveButtonText}>
                      {editingQuote ? 'Update Quote' : 'Add Quote'}
                    </Text>
                  )}
                </Pressable>
              </View>
            </WebScrollView>
          </View>
        </View>
      </Modal>

      {/* Associate Quote Modal */}
      <Modal
        visible={showAssociateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowAssociateModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Associate Quote with Service Request</Text>
              <Pressable
                onPress={() => {
                  setShowAssociateModal(false);
                  setQuoteToAssociate(null);
                  setSelectedServiceRequestId('');
                }}
              >
                <X size={20} color="#9ca3af" />
              </Pressable>
            </View>

            {quoteToAssociate && (
              <View style={styles.associateQuoteInfo}>
                <Text style={styles.associateQuoteName}>
                  {quoteToAssociate.service_provider?.company_name || 'Unknown Provider'}
                </Text>
                {quoteToAssociate.service_type && (
                  <Text style={styles.associateQuoteDetail}>
                    Service Type: {quoteToAssociate.service_type}
                  </Text>
                )}
                {quoteToAssociate.amount !== null && (
                  <Text style={styles.associateQuoteAmount}>
                    {formatCurrency(quoteToAssociate.amount)}
                  </Text>
                )}
              </View>
            )}

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Select Service Request</Text>
              <View style={styles.pickerContainer}>
                <Picker
                  selectedValue={selectedServiceRequestId}
                  onValueChange={(value) => setSelectedServiceRequestId(value)}
                  style={styles.picker}
                >
                  <Picker.Item label="Select a service request..." value="" />
                  {serviceRequests.map((request) => (
                    <Picker.Item key={request.id} label={request.title} value={request.id} />
                  ))}
                </Picker>
              </View>
            </View>

            <View style={styles.modalActions}>
              <Pressable
                style={styles.cancelButton}
                onPress={() => {
                  setShowAssociateModal(false);
                  setQuoteToAssociate(null);
                  setSelectedServiceRequestId('');
                }}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={styles.saveButton}
                onPress={handleAssociateQuote}
                disabled={!selectedServiceRequestId || associatingQuote}
              >
                {associatingQuote ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={styles.saveButtonText}>Associate Quote</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        visible={!!previewImage}
        transparent
        animationType="fade"
        onRequestClose={() => setPreviewImage(null)}
      >
        <Pressable style={styles.imagePreviewOverlay} onPress={() => setPreviewImage(null)}>
          <View style={styles.imagePreviewContainer}>
            <Pressable
              style={styles.imagePreviewClose}
              onPress={() => setPreviewImage(null)}
            >
              <X size={24} color="#ffffff" />
            </Pressable>
            {previewImage && (
              <Image
                source={{ uri: previewImage }}
                style={styles.previewImage}
                resizeMode="contain"
              />
            )}
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    ...shadows.sm,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
  },
  filterButtonText: {
    fontSize: 14,
    color: '#000000',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#000000',
    borderRadius: 6,
  },
  addButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '500',
  },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  refreshButtonText: {
    fontSize: 14,
    color: '#4b5563',
  },
  errorContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fee2e2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
    flex: 1,
  },
  errorClose: {
    color: '#991b1b',
    fontSize: 18,
    marginLeft: 8,
  },
  filtersContainer: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    marginBottom: 24,
  },
  filtersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  filterGroup: {
    minWidth: 150,
    marginBottom: 16,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  filterInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  picker: {
    height: Platform.OS === 'ios' ? 200 : 50,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#000000',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  quotesList: {
    maxHeight: 600,
  },
  quoteCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    marginBottom: 16,
  },
  quoteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  quoteContent: {
    flex: 1,
    marginRight: 12,
  },
  quoteTitleRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  quoteProviderName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
  },
  associatedBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: '#dbeafe',
  },
  associatedText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#2563eb',
  },
  standaloneBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
  },
  standaloneText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#4b5563',
  },
  quoteDetail: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 4,
  },
  quoteDetailLabel: {
    fontWeight: '500',
  },
  quoteDescription: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 8,
  },
  quoteServiceRequest: {
    fontSize: 14,
    color: '#2563eb',
    marginBottom: 8,
  },
  quoteAmount: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  quoteNotes: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 8,
  },
  quoteMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 8,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  attachmentsContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  attachmentItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 6,
    padding: 8,
    marginBottom: 8,
  },
  attachmentContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 8,
  },
  attachmentInfo: {
    flex: 1,
  },
  attachmentName: {
    fontSize: 12,
    fontWeight: '500',
    color: '#111827',
  },
  attachmentSize: {
    fontSize: 12,
    color: '#6b7280',
  },
  attachmentActions: {
    flexDirection: 'row',
    gap: 4,
  },
  attachmentAction: {
    padding: 4,
  },
  quoteActions: {
    flexDirection: 'row',
    gap: 8,
  },
  quoteActionButton: {
    padding: 6,
  },
  quoteCreated: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 24,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000000',
  },
  modalBody: {
    maxHeight: 500,
  },
  modalBodyContent: {
    paddingBottom: 16,
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  required: {
    color: '#dc2626',
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  formHint: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
    paddingTop: 16,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#374151',
    fontWeight: '500',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 6,
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '500',
  },
  associateQuoteInfo: {
    backgroundColor: '#f9fafb',
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
  },
  associateQuoteName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 4,
  },
  associateQuoteDetail: {
    fontSize: 12,
    color: '#4b5563',
  },
  associateQuoteAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    marginTop: 4,
  },
  imagePreviewOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  imagePreviewContainer: {
    position: 'relative',
    maxWidth: '100%',
    maxHeight: '90%',
  },
  imagePreviewClose: {
    position: 'absolute',
    top: -40,
    right: 0,
    zIndex: 1,
  },
  previewImage: {
    width: '100%',
    maxWidth: 800,
    height: '100%',
    maxHeight: 600,
    borderRadius: 8,
  },
});

export default MarketResearchQuotes;
